/**
 * Admin Agent API: chat session bootstrap and approval of the changes the agent proposes.
 */

import axiosInstance from '../lib/axios';

export type AdminActionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'executed'
  | 'failed';

export interface AdminAgentSession {
  agentPublicId: string;
  conversationPublicId: string;
  sessionId: number;
}

export interface AdminAction {
  publicId: string;
  operationId: string;
  method: string;
  path: string;
  query: unknown;
  body: unknown;
  riskLevel: string;
  reason: string | null;
  preview: unknown;
  status: AdminActionStatus;
  responseStatus: number | null;
  resultSummary: string | null;
  expiresAt: string;
  decidedAt: string | null;
  executedAt: string | null;
  createdAt: string;
}

export interface AdminActionActor {
  publicId: string;
  email: string;
  fullName: string | null;
}

export interface AdminActionHistoryItem extends AdminAction {
  requestedBy: AdminActionActor;
  approver: AdminActionActor | null;
}

export interface AdminActionHistoryFilters {
  status?: AdminActionStatus;
  riskLevel?: 'write' | 'destructive';
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminActionHistoryPage {
  items: AdminActionHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminConversation {
  conversationPublicId: string;
  title: string | null;
  createdAt: string;
  lastMessageAt: string | null;
}

export interface AdminConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

export interface AdminSessionRequest {
  /** Start a fresh conversation instead of resuming the latest one. */
  newConversation?: boolean;
  /** Resume this specific conversation. */
  conversationPublicId?: string;
}

const BASE_PATH = '/v1/admin/agent';

export const DESTRUCTIVE_RISK_LEVEL = 'destructive';

export const adminAgentService = {
  async createSession(request: AdminSessionRequest = {}): Promise<AdminAgentSession> {
    const response = await axiosInstance.post<{ data: AdminAgentSession }>(
      `${BASE_PATH}/session`,
      request
    );
    return response.data.data;
  },

  async createSocketTicket(
    conversationPublicId: string
  ): Promise<{ ticket: string; expiresInSeconds: number }> {
    const response = await axiosInstance.post<{
      data: { ticket: string; expiresInSeconds: number };
    }>(`${BASE_PATH}/socket-ticket`, { conversationPublicId });
    return response.data.data;
  },

  async listConversations(): Promise<AdminConversation[]> {
    const response = await axiosInstance.get<{ data: AdminConversation[] }>(
      `${BASE_PATH}/conversations`
    );
    return response.data.data;
  },

  async renameConversation(
    conversationPublicId: string,
    title: string
  ): Promise<AdminConversation> {
    const response = await axiosInstance.patch<{ data: AdminConversation }>(
      `${BASE_PATH}/conversations/${conversationPublicId}`,
      { title }
    );
    return response.data.data;
  },

  async getConversationMessages(conversationPublicId: string): Promise<AdminConversationMessage[]> {
    const response = await axiosInstance.get<{ data: AdminConversationMessage[] }>(
      `${BASE_PATH}/conversations/${conversationPublicId}/messages`
    );
    return response.data.data;
  },

  async listActions(
    filters: { status?: AdminActionStatus; conversationPublicId?: string } = {}
  ): Promise<AdminAction[]> {
    const response = await axiosInstance.get<{ data: AdminAction[] }>(`${BASE_PATH}/actions`, {
      params: filters,
    });
    return response.data.data;
  },

  async listHistory(filters: AdminActionHistoryFilters): Promise<AdminActionHistoryPage> {
    const response = await axiosInstance.get<{ data: AdminActionHistoryPage }>(
      `${BASE_PATH}/history`,
      { params: filters }
    );
    return response.data.data;
  },

  async approveAction(publicId: string, confirmed: boolean): Promise<AdminAction> {
    const response = await axiosInstance.post<{ data: AdminAction }>(
      `${BASE_PATH}/actions/${publicId}/approve`,
      { confirmed }
    );
    return response.data.data;
  },

  async rejectAction(publicId: string): Promise<AdminAction> {
    const response = await axiosInstance.post<{ data: AdminAction }>(
      `${BASE_PATH}/actions/${publicId}/reject`,
      {}
    );
    return response.data.data;
  },
};
